import axios from 'axios';
import { Configuration, AuthenticationApi, MFAApi } from './lib/index.js';
const fromProcess = typeof process !== 'undefined' ? process.env?.['VITE_IDP_BASE_URL'] : undefined;
const fromImportMeta = globalThis.importMetaEnv
    ?.VITE_IDP_BASE_URL;
const defaultBasePath = fromProcess ?? fromImportMeta ?? 'https://idp.uat.digiwedge.com';
function sanitizeBase(input) {
    if (!input)
        return input;
    const trimmed = input.trim();
    if (!trimmed)
        return undefined;
    if (trimmed === '/')
        return '';
    return trimmed.replace(/\/$/, '');
}
function getCookie(name) {
    if (typeof document === 'undefined')
        return null;
    const match = document.cookie
        .split('; ')
        .find((entry) => entry.startsWith(`${name}=`));
    if (!match)
        return null;
    const value = match.slice(name.length + 1);
    return value ? decodeURIComponent(value) : null;
}
function isUnsafeMethod(method) {
    if (!method)
        return false;
    const upper = method.toUpperCase();
    return upper === 'POST' || upper === 'PUT' || upper === 'PATCH' || upper === 'DELETE';
}
/**
 * Create a configured IDP client. The caller supplies callbacks for token
 * management so this library remains free of global state.
 */
export const createIdpClient = ({ basePath = defaultBasePath, tokenDelivery = 'json', getAccessToken, getRefreshToken, getJti, login, logout, getAudience, }) => {
    const sanitized = sanitizeBase(basePath);
    const normalizedBase = sanitized ?? '';
    const axiosBaseUrl = sanitized ? sanitized : undefined;
    const idpAxios = axios.create({ baseURL: axiosBaseUrl, withCredentials: true });
    const cookieOnly = tokenDelivery === 'cookie';
    let isRefreshing = false;
    let pendingRequests = [];
    let refreshDisabled = false;
    const processQueue = (token) => {
        pendingRequests.forEach((cb) => cb(token || undefined));
        pendingRequests = [];
    };
    idpAxios.interceptors.request.use((config) => {
        config.headers = config.headers ?? {};
        if (!cookieOnly) {
            const token = getAccessToken();
            if (token && config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        // Inject audience header for login/refresh endpoints
        const url = (config.url ?? '').toString().toLowerCase();
        if (config.headers && (url.includes('/auth/login') || url.includes('/auth/refresh'))) {
            const aud = getAudience?.() ?? 'teetime-web';
            config.headers['X-Client-Audience'] = aud;
        }
        if (config.headers &&
            cookieOnly &&
            (url.includes('/auth/login') ||
                url.includes('/auth/refresh') ||
                url.includes('/auth/mfa/verify-login'))) {
            config.headers['X-Token-Delivery'] = 'cookie';
        }
        if (config.headers && isUnsafeMethod(config.method)) {
            const xsrf = getCookie('XSRF-TOKEN');
            if (xsrf) {
                config.headers['X-XSRF-Token'] = xsrf;
            }
        }
        // Browsers forbid setting User-Agent headers; ensure none slip through
        if (config.headers) {
            const headers = config.headers;
            if (Object.prototype.hasOwnProperty.call(headers, 'user-agent')) {
                delete headers['user-agent'];
            }
            if (Object.prototype.hasOwnProperty.call(headers, 'User-Agent')) {
                delete headers['User-Agent'];
            }
        }
        return config;
    });
    const config = new Configuration({ basePath: normalizedBase });
    if (typeof window !== 'undefined') {
        const headers = (config.baseOptions?.headers ?? {});
        delete headers['User-Agent'];
        delete headers['user-agent'];
        config.baseOptions = { ...config.baseOptions, headers };
    }
    const authApi = new AuthenticationApi(config, normalizedBase, idpAxios);
    const mfaApi = new MFAApi(config, normalizedBase, idpAxios);
    const verifyEmail = (params) => idpAxios.post('/api/auth/verify-email', params);
    const requestPasswordReset = async (email) => {
        await idpAxios.post('/api/auth/forgot-password', { email });
    };
    const resetPassword = async (params) => {
        await idpAxios.post('/api/auth/reset-password', params);
    };
    // Account lifecycle methods (#10496)
    const requestDeletion = async (reason) => {
        await idpAxios.post('/api/auth/account/delete-request', { reason });
    };
    const confirmDeletion = async (code) => {
        await idpAxios.delete('/api/auth/account', { data: { code } });
    };
    const requestRestore = async (email) => {
        await idpAxios.post('/api/auth/account/restore-request', { email });
    };
    const confirmRestore = async (email, code) => {
        await idpAxios.post('/api/auth/account/restore', { email, code });
    };
    const exportAccountData = async (format = 'json') => {
        const response = await idpAxios.get('/api/auth/account/export', {
            params: { format },
        });
        return response.data;
    };
    idpAxios.interceptors.response.use((response) => response, async (error) => {
        const originalRequest = error.config;
        const url = (originalRequest?.url ?? '').toString().toLowerCase();
        if (url.includes('/auth/login')) {
            return Promise.reject(error);
        }
        if (originalRequest?.url?.includes('auth/refresh')) {
            return Promise.reject(error);
        }
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            if (refreshDisabled) {
                logout();
                return Promise.reject(error);
            }
            const refreshToken = getRefreshToken();
            const jti = getJti();
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    pendingRequests.push((newToken) => {
                        if (!newToken && !cookieOnly)
                            return reject(error);
                        if (newToken) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        }
                        else if (originalRequest.headers?.Authorization) {
                            delete originalRequest.headers.Authorization;
                        }
                        resolve(idpAxios(originalRequest));
                    });
                });
            }
            isRefreshing = true;
            try {
                let refreshResponse;
                if (cookieOnly) {
                    refreshResponse = await idpAxios.post('/api/auth/refresh', {});
                }
                else if (refreshToken) {
                    refreshResponse = await authApi.authControllerRefresh('', {
                        refreshToken,
                        jti,
                    });
                }
                else {
                    // Cookie-backed refresh (no body); cookies sent via withCredentials
                    refreshResponse = await idpAxios.post('/api/auth/refresh', {});
                }
                const nextAccessToken = refreshResponse?.data?.accessToken;
                if (!cookieOnly && nextAccessToken) {
                    login(nextAccessToken, refreshResponse.data?.refreshToken ?? '', refreshResponse.data?.jti ?? '', true);
                }
                processQueue(nextAccessToken ?? null);
                if (!cookieOnly && nextAccessToken) {
                    originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
                }
                else if (originalRequest.headers?.Authorization) {
                    delete originalRequest.headers.Authorization;
                }
                return idpAxios(originalRequest);
            }
            catch (refreshError) {
                refreshDisabled = true;
                logout();
                processQueue(null);
                return Promise.reject(refreshError);
            }
            finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    });
    return {
        authApi,
        mfaApi,
        verifyEmail,
        axios: idpAxios,
        requestPasswordReset,
        resetPassword,
        tokenDelivery,
        // Account lifecycle methods (#10496)
        requestDeletion,
        confirmDeletion,
        requestRestore,
        confirmRestore,
        exportAccountData,
    };
};
//# sourceMappingURL=client.js.map